import React, { useState, useMemo } from 'react';
import { useNavigate } from 'react-router-dom';
import { Code, Copy, Check, ChevronDown, ChevronRight } from 'lucide-react';
import { toast } from 'sonner';

export default function APIDocsPage() {
  const navigate = useNavigate();
  const [copiedCode, setCopiedCode] = useState('');
  const [expandedSections, setExpandedSections] = useState(['auth']);

  // Dynamic API URL - uses current origin for environment-agnostic docs
  const API_BASE_URL = useMemo(() => {
    // In production, this will be the actual domain (commerceindex.com)
    // In preview, this will be the preview URL
    return window.location.origin;
  }, []);

  const copyToClipboard = (code, id) => {
    navigator.clipboard.writeText(code);
    setCopiedCode(id);
    toast.success('Copied to clipboard!');
    setTimeout(() => setCopiedCode(''), 2000);
  };

  const toggleSection = (section) => {
    setExpandedSections(prev => 
      prev.includes(section) 
        ? prev.filter(s => s !== section)
        : [...prev, section]
    );
  };

  const CodeBlock = ({ code, language = 'bash', id }) => (
    <div className="relative bg-gray-900 rounded-lg p-4 my-4">
      <div className="flex items-center justify-between mb-2">
        <span className="text-xs text-gray-400 uppercase">{language}</span>
        <button
          onClick={() => copyToClipboard(code, id)}
          className="text-gray-400 hover:text-white transition-colors"
        >
          {copiedCode === id ? <Check className="w-4 h-4 text-green-500" /> : <Copy className="w-4 h-4" />}
        </button>
      </div>
      <pre className="text-sm text-gray-100 overflow-x-auto">
        <code>{code}</code>
      </pre>
    </div>
  );

  const Section = ({ id, title, children }) => {
    const isExpanded = expandedSections.includes(id);
    return (
      <div className="border border-gray-200 rounded-xl mb-6 overflow-hidden">
        <button
          onClick={() => toggleSection(id)}
          className="w-full flex items-center justify-between p-6 bg-gray-50 hover:bg-gray-100 transition-colors"
        >
          <h3 className="text-xl font-bold text-gray-900">{title}</h3>
          {isExpanded ? <ChevronDown className="w-5 h-5" /> : <ChevronRight className="w-5 h-5" />}
        </button>
        {isExpanded && (
          <div className="p-6 border-t border-gray-200">
            {children}
          </div>
        )}
      </div>
    );
  };

  return (
    <div className="min-h-screen bg-white">
      <div className="max-w-5xl mx-auto px-6 py-16">
        {/* Header */}
        <div className="mb-12">
          <h1 className="text-5xl font-bold text-gray-900 mb-4">API Documentation</h1>
          <p className="text-xl text-gray-700 mb-6">
            Build powerful commerce applications with Commerce Index intelligence.
          </p>
          <div className="flex gap-4 flex-wrap">
            <div className="px-4 py-2 bg-blue-100 text-blue-700 rounded-lg">
              <span className="font-semibold">Base URL:</span> {API_BASE_URL}
            </div>
            <div className="px-4 py-2 bg-green-100 text-green-700 rounded-lg">
              <span className="font-semibold">Version:</span> v1.0
            </div>
          </div>
        </div>

        {/* Quick Start */}
        <div className="bg-gradient-to-r from-blue-600 to-blue-700 rounded-2xl p-8 mb-12 text-white">
          <h2 className="text-3xl font-bold mb-4">Quick Start</h2>
          <p className="text-blue-100 mb-6">Get your API key from the Enterprise dashboard and start making requests in minutes.</p>
          <CodeBlock 
            language="bash" 
            id="quickstart"
            code={`curl -X GET "${API_BASE_URL}/api/consumer/deals?limit=10" \\
  -H "Authorization: Bearer YOUR_API_KEY"`}
          />
        </div>

        {/* Authentication */}
        <Section id="auth" title="🔐 Authentication">
          <p className="text-gray-700 mb-4">
            All API requests require authentication using Bearer tokens. Include your API key in the Authorization header.
          </p>
          
          <h4 className="text-lg font-bold text-gray-900 mb-3">Consumer Signup</h4>
          <CodeBlock
            language="bash"
            id="signup"
            code={`curl -X POST "${API_BASE_URL}/api/consumer/auth/signup/email" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "securepass123",
    "name": "John Doe"
  }'`}
          />

          <h4 className="text-lg font-bold text-gray-900 mb-3 mt-6">Consumer Login</h4>
          <CodeBlock
            language="bash"
            id="login"
            code={`curl -X POST "${API_BASE_URL}/api/consumer/auth/login/email" \\
  -H "Content-Type: application/json" \\
  -d '{
    "email": "user@example.com",
    "password": "securepass123"
  }'`}
          />

          <div className="bg-blue-50 border border-blue-200 rounded-lg p-4 mt-4">
            <p className="text-sm text-gray-900">
              <strong className="text-blue-700">Response:</strong> Returns session_token to use in subsequent requests
            </p>
          </div>
        </Section>

        {/* Deals API */}
        <Section id="deals" title="🏷️ Deals API">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Get Deals</h4>
          <p className="text-gray-700 mb-4">Retrieve deals with filters, sorting, and pagination.</p>
          <CodeBlock
            language="bash"
            id="get-deals"
            code={`curl -X GET "${API_BASE_URL}/api/consumer/deals?category=Electronics&sort_by=ci_score&limit=20" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
          />

          <h5 className="font-semibold text-gray-900 mt-4 mb-2">Query Parameters:</h5>
          <ul className="space-y-2 text-sm text-gray-900 mb-6">
            <li><code className="bg-gray-100 px-2 py-1 rounded">category</code> - Filter by category</li>
            <li><code className="bg-gray-100 px-2 py-1 rounded">min_price</code> - Minimum price filter</li>
            <li><code className="bg-gray-100 px-2 py-1 rounded">max_price</code> - Maximum price filter</li>
            <li><code className="bg-gray-100 px-2 py-1 rounded">merchant</code> - Filter by merchant name</li>
            <li><code className="bg-gray-100 px-2 py-1 rounded">sort_by</code> - Sort by: ci_score, discount, price, date</li>
            <li><code className="bg-gray-100 px-2 py-1 rounded">search</code> - Search query</li>
            <li><code className="bg-gray-100 px-2 py-1 rounded">limit</code> - Results per page (max 100)</li>
          </ul>

          <h4 className="text-lg font-bold text-gray-900 mb-3 mt-6">Get Deal Details</h4>
          <CodeBlock
            language="bash"
            id="get-deal"
            code={`curl -X GET "${API_BASE_URL}/api/consumer/deals/{deal_id}" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
          />
        </Section>

        {/* Watchlist API */}
        <Section id="watchlist" title="❤️ Watchlist & Alerts">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Add to Watchlist</h4>
          <CodeBlock
            language="bash"
            id="add-watchlist"
            code={`curl -X POST "${API_BASE_URL}/api/consumer/watchlist/add" \\
  -H "Authorization: Bearer YOUR_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{"deal_id": "deal-uuid-here"}'`}
          />

          <h4 className="text-lg font-bold text-gray-900 mb-3 mt-6">Get Watchlist</h4>
          <CodeBlock
            language="bash"
            id="get-watchlist"
            code={`curl -X GET "${API_BASE_URL}/api/consumer/watchlist/" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
          />

          <h4 className="text-lg font-bold text-gray-900 mb-3 mt-6">Get Price Alerts</h4>
          <CodeBlock
            language="bash"
            id="get-alerts"
            code={`curl -X GET "${API_BASE_URL}/api/consumer/watchlist/alerts" \\
  -H "Authorization: Bearer YOUR_TOKEN"`}
          />
        </Section>

        {/* Merchant API */}
        <Section id="merchant" title="🏪 Merchant API">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Get Merchant Products</h4>
          <CodeBlock
            language="bash"
            id="merchant-products"
            code={`curl -X GET "${API_BASE_URL}/api/dashboard/products?limit=50" \\
  -H "Authorization: Bearer YOUR_MERCHANT_TOKEN"`}
          />

          <h4 className="text-lg font-bold text-gray-900 mb-3 mt-6">Create Deal</h4>
          <CodeBlock
            language="bash"
            id="create-deal"
            code={`curl -X POST "${API_BASE_URL}/api/dashboard/deals" \\
  -H "Authorization: Bearer YOUR_MERCHANT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "product_id": "product-uuid",
    "discount_type": "percentage",
    "discount_value": 20,
    "coupon_code": "SAVE20",
    "end_date": "2025-12-31T23:59:59Z"
  }'`}
          />
        </Section>

        {/* Shopify Integration */}
        <Section id="shopify" title="🛍️ Shopify Integration">
          <h4 className="text-lg font-bold text-gray-900 mb-3">Connect Shopify Store</h4>
          <CodeBlock
            language="bash"
            id="shopify-connect"
            code={`curl -X POST "${API_BASE_URL}/api/shopify/connect" \\
  -H "Authorization: Bearer YOUR_MERCHANT_TOKEN" \\
  -H "Content-Type: application/json" \\
  -d '{
    "store_url": "mystore.myshopify.com",
    "api_key": "shpat_xxxxxxxxxxxxx"
  }'`}
          />

          <h4 className="text-lg font-bold text-gray-900 mb-3 mt-6">Trigger Product Sync</h4>
          <CodeBlock
            language="bash"
            id="shopify-sync"
            code={`curl -X POST "${API_BASE_URL}/api/shopify/sync" \\
  -H "Authorization: Bearer YOUR_MERCHANT_TOKEN"`}
          />
        </Section>

        {/* Webhooks */}
        <Section id="webhooks" title="🔔 Webhooks">
          <p className="text-gray-700 mb-4">
            Subscribe to events and receive real-time notifications when prices change, deals expire, or new deals match criteria.
          </p>
          
          <h4 className="text-lg font-bold text-gray-900 mb-3">Available Events:</h4>
          <ul className="space-y-2 text-sm text-gray-900 mb-6">
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
              <code className="bg-gray-100 px-2 py-1 rounded">price.dropped</code> - Triggered when a watched deal's price decreases
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
              <code className="bg-gray-100 px-2 py-1 rounded">deal.expiring</code> - Triggered 24h before deal expires
            </li>
            <li className="flex items-center gap-2">
              <span className="w-2 h-2 bg-blue-600 rounded-full"></span>
              <code className="bg-gray-100 px-2 py-1 rounded">deal.created</code> - Triggered when new deal is added
            </li>
          </ul>

          <h4 className="text-lg font-bold text-gray-900 mb-3">Webhook Payload Example:</h4>
          <CodeBlock
            language="json"
            id="webhook-payload"
            code={`{
  "event": "price.dropped",
  "timestamp": "2025-12-10T05:30:00Z",
  "data": {
    "deal_id": "abc-123",
    "product_title": "Sony Headphones",
    "old_price": 299.99,
    "new_price": 249.99,
    "ci_score": 875
  }
}`}
          />
        </Section>

        {/* Rate Limits */}
        <Section id="rate-limits" title="⚡ Rate Limits">
          <div className="space-y-4">
            <div className="p-4 bg-gray-50 rounded-lg">
              <h5 className="font-semibold text-gray-900 mb-2">Free Tier</h5>
              <p className="text-sm text-gray-900">60 requests per minute</p>
            </div>
            <div className="p-4 bg-blue-50 rounded-lg border border-blue-200">
              <h5 className="font-semibold text-gray-900 mb-2">Pro Tier</h5>
              <p className="text-sm text-gray-900">300 requests per minute</p>
            </div>
            <div className="p-4 bg-purple-50 rounded-lg border border-purple-200">
              <h5 className="font-semibold text-gray-900 mb-2">Enterprise Tier</h5>
              <p className="text-sm text-gray-900">Unlimited (custom SLA)</p>
            </div>
          </div>
        </Section>

        {/* SDKs */}
        <Section id="sdks" title="📦 SDKs & Libraries">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            <div className="p-6 bg-gray-50 rounded-lg">
              <h5 className="font-bold text-gray-900 mb-3">JavaScript/Node.js</h5>
              <CodeBlock
                language="bash"
                id="npm-install"
                code="npm install @commerceindex/sdk"
              />
              <CodeBlock
                language="javascript"
                id="js-example"
                code={`import CommerceIndex from '@commerceindex/sdk';

const ci = new CommerceIndex({
  apiKey: 'YOUR_API_KEY'
});

const deals = await ci.deals.list({
  category: 'Electronics',
  limit: 20
});`}
              />
            </div>

            <div className="p-6 bg-gray-50 rounded-lg">
              <h5 className="font-bold text-gray-900 mb-3">Python</h5>
              <CodeBlock
                language="bash"
                id="pip-install"
                code="pip install commerceindex"
              />
              <CodeBlock
                language="python"
                id="python-example"
                code={`from commerceindex import Client

ci = Client(api_key='YOUR_API_KEY')

deals = ci.deals.list(
    category='Electronics',
    limit=20
)`}
              />
            </div>

            <div className="p-6 bg-gray-50 rounded-lg">
              <h5 className="font-bold text-gray-900 mb-3">Chrome Extension</h5>
              <p className="text-sm text-gray-900 mb-3">Build browser extensions using our SDK</p>
              <button
                onClick={() => window.open('/chrome-extension/README.md', '_blank')}
                className="px-4 py-2 bg-purple-600 text-white rounded-lg hover:bg-purple-700 transition-colors font-semibold text-sm"
              >
                View Extension Docs
              </button>
            </div>
          </div>
        </Section>

        {/* Response Codes */}
        <Section id="errors" title="❌ Error Handling">
          <div className="space-y-3">
            <div className="flex items-start gap-4 p-3 bg-gray-50 rounded-lg">
              <code className="text-lg font-bold text-gray-900 w-12">200</code>
              <div>
                <div className="font-semibold text-gray-900">OK</div>
                <div className="text-sm text-gray-700">Request successful</div>
              </div>
            </div>
            <div className="flex items-start gap-4 p-3 bg-red-50 rounded-lg">
              <code className="text-lg font-bold text-red-600 w-12">401</code>
              <div>
                <div className="font-semibold text-gray-900">Unauthorized</div>
                <div className="text-sm text-gray-700">Invalid or missing API key</div>
              </div>
            </div>
            <div className="flex items-start gap-4 p-3 bg-orange-50 rounded-lg">
              <code className="text-lg font-bold text-orange-600 w-12">429</code>
              <div>
                <div className="font-semibold text-gray-900">Too Many Requests</div>
                <div className="text-sm text-gray-700">Rate limit exceeded</div>
              </div>
            </div>
            <div className="flex items-start gap-4 p-3 bg-yellow-50 rounded-lg">
              <code className="text-lg font-bold text-yellow-600 w-12">500</code>
              <div>
                <div className="font-semibold text-gray-900">Internal Server Error</div>
                <div className="text-sm text-gray-700">Something went wrong on our end</div>
              </div>
            </div>
          </div>
        </Section>

        {/* Support */}
        <div className="mt-12 bg-gradient-to-r from-purple-600 to-purple-700 rounded-2xl p-8 text-white text-center">
          <h2 className="text-3xl font-bold mb-4">Need Help?</h2>
          <p className="text-purple-100 mb-6">
            Our developer support team is here to help you integrate Commerce Index into your application.
          </p>
          <div className="flex gap-4 justify-center flex-wrap">
            <button
              onClick={() => window.open('mailto:api@commerceindex.com', '_blank')}
              className="px-6 py-3 bg-white text-purple-600 rounded-lg hover:bg-gray-100 transition-colors font-semibold"
            >
              Email Support
            </button>
            <button
              onClick={() => navigate('/merchant/login')}
              className="px-6 py-3 border-2 border-white text-white rounded-lg hover:bg-purple-800 transition-colors font-semibold"
            >
              Get Enterprise Access
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}
